<main>
  <loading-overlay [isLoading]="isLoading() > 0" />
  <p-toast position="bottom-right" key="br" />

  <header>
    <div class="headline">
      <h1>{{ headerTitle() }}</h1>
      <i
        class="pi pi-info-circle text-color-secondary"
        tooltipPosition="bottom"
        [pTooltip]="
          'ApprovalPolicies_Note'
            | translateWF
              : 'An inactive policy is never evaluated. Each document type has one default policy; a fund or trip may choose another that applies to that type. Changing a policy does not affect documents already submitted: their approvers were frozen when they were sent.'
        "
      ></i>
      @if (policy(); as current) {
        @if (current.isActive) {
          <p-tag severity="success" [value]="'ApprovalPolicies_Status_Active' | translateWF: 'Active'" />
        } @else {
          <p-tag severity="secondary" [value]="'ApprovalPolicies_Status_Inactive' | translateWF: 'Inactive'" />
        }
      }
    </div>
    <div class="buttons">
      @if (canEdit) {
        <p-splitbutton [label]="'Save' | translateWF: 'Save'" (onClick)="save(false)" [model]="saveMenuItems" />
      }
      <p-button variant="outlined" (onClick)="back()">{{ 'Back' | translateWF: 'Back' }}</p-button>
    </div>
  </header>

  <p-breadcrumb class="max-w-full" [model]="menuItems()">
    <ng-template #item let-item>
      @if (item.route) {
        <a [routerLink]="item.route" class="p-breadcrumb-item-link">
          <span [ngClass]="[item.icon ? item.icon : '', 'text-color']"></span>
          <span class="text-primary">{{ item.label }}</span>
        </a>
      } @else {
        <a class="p-breadcrumb-current-item-link">
          <span class="text-color">{{ item.label }}</span>
        </a>
      }
    </ng-template>
  </p-breadcrumb>

  <div class="content">
    <form [formGroup]="form" class="grid grid-cols-12 gap-5">
      <div class="col-span-12 xl:col-span-8 flex flex-col gap-5">
        <!-- Policy data -->
        <p-panel [header]="'ApprovalPolicies_Section_Data' | translateWF: 'Policy data'">
          <div class="grid grid-cols-4 gap-4">
            <div class="col-span-4 md:col-span-2 flex flex-col gap-2">
              <div class="flex items-center justify-between gap-2">
                <label for="ap-name">{{ fieldLabels.name | translateWF: 'Name' }} <span class="text-red-700">*</span></label>
                <span class="flex items-center gap-2">
                  <i class="pi pi-language"></i>
                  {{ 'Common_Spanish_Translate' | translateWF: 'Spanish Translate' }}
                  <p-toggleswitch [formControl]="spanishTranslate" />
                </span>
              </div>
              <ng-container formGroupName="name">
                <p-inputgroup>
                  <p-inputgroup-addon>EN</p-inputgroup-addon>
                  <input
                    id="ap-name"
                    pInputText
                    formControlName="en"
                    [ngClass]="{ 'ng-invalid ng-dirty': form.controls.name.controls.en.invalid && form.controls.name.controls.en.touched }"
                  />
                </p-inputgroup>
                @if (spanishTranslate.value) {
                  <p-inputgroup>
                    <p-inputgroup-addon>ES</p-inputgroup-addon>
                    <input
                      pInputText
                      formControlName="es"
                      [ngClass]="{ 'ng-invalid ng-dirty': form.controls.name.controls.es.invalid && form.controls.name.controls.es.touched }"
                    />
                  </p-inputgroup>
                }
              </ng-container>
            </div>
            <div class="col-span-2 md:col-span-1 flex flex-col gap-2">
              <label for="ap-active">{{ fieldLabels.isActive | translateWF: 'Status' }}</label>
              <div class="flex items-center gap-2 h-10">
                <p-toggleswitch inputId="ap-active" formControlName="isActive" />
                <span>
                  @if (form.controls.isActive.value) {
                    {{ 'ApprovalPolicies_Status_Active' | translateWF: 'Active' }}
                  } @else {
                    {{ 'ApprovalPolicies_Status_Inactive' | translateWF: 'Inactive' }}
                  }
                </span>
              </div>
            </div>
            <div class="col-span-2 md:col-span-1 flex flex-col gap-2">
              <label for="ap-minimum">{{ fieldLabels.minimumApprovals | translateWF: 'Minimum approvals' }} <span class="text-red-700">*</span></label>
              <p-inputnumber
                inputId="ap-minimum"
                formControlName="minimumApprovals"
                [min]="0"
                [showButtons]="true"
                [ngClass]="{ 'ng-invalid ng-dirty': form.controls.minimumApprovals.invalid && form.controls.minimumApprovals.touched }"
              />
              <small>{{ 'ApprovalPolicies_MinimumApprovals_Hint' | translateWF: 'How many optional approvers must approve.' }}</small>
            </div>
            <div class="col-span-4 md:col-span-2 flex flex-col gap-2">
              <label for="ap-applies">{{ fieldLabels.appliesTo | translateWF: 'Applies to' }} <span class="text-red-700">*</span></label>
              <p-multiselect
                inputId="ap-applies"
                formControlName="appliesTo"
                [options]="entityOptions"
                optionLabel="label"
                optionValue="value"
                display="chip"
                [showToggleAll]="false"
                [ngClass]="{ 'ng-invalid ng-dirty': form.controls.appliesTo.invalid && form.controls.appliesTo.touched }"
              />
              <small>{{ 'ApprovalPolicies_AppliesTo_Hint' | translateWF: 'Document types that can use this policy.' }}</small>
            </div>
            <div class="col-span-4 md:col-span-2 flex flex-col gap-2">
              <label for="ap-default">{{ fieldLabels.defaultEntities | translateWF: 'Default for' }}</label>
              <p-multiselect
                inputId="ap-default"
                formControlName="defaultEntities"
                [options]="defaultOptions()"
                optionLabel="label"
                optionValue="value"
                display="chip"
                [showToggleAll]="false"
              />
              <small>
                {{ 'ApprovalPolicies_DefaultFor_Hint' | translateWF: 'Used when the document does not choose a policy. Only among the types in "Applies to".' }}
              </small>
              @for (move of preview()?.defaultMoves ?? []; track move.entityDefinitionCode) {
                <p-message severity="warn" size="small" icon="pi pi-exclamation-triangle">
                  {{ entityLabel(move.entityDefinitionCode) }}
                  {{ 'ApprovalPolicies_DefaultMove' | translateWF: 'will stop being the default of' }}
                  «{{ localized(move.fromPolicyName) }}».
                </p-message>
              }
            </div>
          </div>
        </p-panel>

        <!-- Approvers -->
        <p-panel [header]="fieldLabels.approvers | translateWF: 'Approvers'">
          <div class="flex flex-col gap-4">
            <p-table [value]="approvers.controls" [rowHover]="true">
              <ng-template #header>
                <tr>
                  <th class="w-[9rem]">{{ approverLabels.type | translateWF: 'Type' }}</th>
                  <th>{{ approverLabels.value | translateWF: 'User or role' }}</th>
                  <th class="w-[7rem] text-right">{{ approverLabels.order | translateWF: 'Order' }}</th>
                  <th class="w-[7rem] !text-center">{{ approverLabels.isRequired | translateWF: 'Required' }}</th>
                  <th class="w-[13rem] text-right">{{ approverLabels.fromAmountUsd | translateWF: 'From amount' }}</th>
                  @if (canEdit) {
                    <th class="w-[4rem]"></th>
                  }
                </tr>
              </ng-template>
              <ng-template #body let-row let-i="rowIndex">
                <tr [formGroup]="row">
                  <td>
                    <p-select
                      formControlName="type"
                      [options]="approverTypeOptions"
                      optionLabel="label"
                      optionValue="value"
                      appendTo="body"
                      class="w-full"
                      (onChange)="onTypeChange(row)"
                    />
                  </td>
                  <td>
                    <p-select
                      formControlName="value"
                      [options]="isRole(row) ? roleOptions() : userOptions()"
                      optionLabel="label"
                      optionValue="value"
                      [filter]="true"
                      filterBy="label"
                      appendTo="body"
                      class="w-full"
                      [placeholder]="isRole(row) ? ('ApprovalPolicies_Pick_Role' | translateWF: 'Select a role') : ('ApprovalPolicies_Pick_User' | translateWF: 'Select a user')"
                      [ariaLabel]="(approverLabels.value | translateWF: 'User or role') + ' ' + (i + 1)"
                      [ngClass]="{ 'ng-invalid ng-dirty': row.controls.value.invalid && row.controls.value.touched }"
                    />
                  </td>
                  <td class="text-right">
                    <p-inputnumber
                      formControlName="order"
                      [min]="1"
                      [useGrouping]="false"
                      inputStyleClass="w-[4.5rem] text-right"
                      [ngClass]="{ 'ng-invalid ng-dirty': row.controls.order.invalid && row.controls.order.touched }"
                    />
                  </td>
                  <td class="!text-center">
                    <p-checkbox formControlName="isRequired" [binary]="true" />
                  </td>
                  <td class="text-right">
                    <p-inputgroup>
                      <p-inputnumber
                        formControlName="fromAmountUsd"
                        mode="decimal"
                        [min]="0"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="2"
                        locale="en-US"
                        [placeholder]="'ApprovalPolicies_Always' | translateWF: 'Always'"
                        inputStyleClass="text-right"
                        [ngClass]="{ 'ng-invalid ng-dirty': row.controls.fromAmountUsd.invalid && row.controls.fromAmountUsd.touched }"
                      />
                      <p-inputgroup-addon>USD</p-inputgroup-addon>
                    </p-inputgroup>
                  </td>
                  @if (canEdit) {
                    <td class="!text-center">
                      <p-button
                        icon="pi pi-trash"
                        severity="danger"
                        [text]="true"
                        [rounded]="true"
                        [ariaLabel]="'ApprovalPolicies_Remove_Approver' | translateWF: 'Remove approver'"
                        (onClick)="removeApprover(i)"
                      />
                    </td>
                  }
                </tr>
              </ng-template>
            </p-table>

            <div class="flex items-center justify-between gap-4">
              @if (canEdit) {
                <p-button [label]="'ApprovalPolicies_Add_Approver' | translateWF: 'Add approver'" icon="pi pi-plus" variant="text" (onClick)="addApprover()" />
              }
              <small>{{ 'ApprovalPolicies_Order_Hint' | translateWF: 'Empty order = they approve in parallel.' }}</small>
            </div>

            <!-- Pre-save verification (POST approval-policy/preview) -->
            @if (preview(); as p) {
              <div class="flex flex-col gap-2">
                <strong>{{ 'ApprovalPolicies_Verification' | translateWF: 'Verification (before saving)' }}</strong>
                @if (showErrors()) {
                  @for (error of p.errors; track $index) {
                    <p-message severity="error" icon="pi pi-times-circle">{{ localized(error) }}</p-message>
                  }
                }
                @for (band of p.bands; track band.fromAmountUsd) {
                  <p-message [severity]="band.canBeMet ? 'success' : 'warn'" [icon]="band.canBeMet ? 'pi pi-check-circle' : 'pi pi-exclamation-triangle'">
                    {{ localized(band) }}
                  </p-message>
                }
              </div>
            }
          </div>
        </p-panel>
      </div>

      <!-- Simulator -->
      <div class="col-span-12 xl:col-span-4 flex flex-col gap-5">
        <p-panel [header]="'ApprovalPolicies_Simulator' | translateWF: 'Simulator'">
          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-2">
              <label for="ap-sim-amount">{{ 'ApprovalPolicies_Simulator_Amount' | translateWF: 'Document amount' }}</label>
              <p-inputgroup>
                <p-inputnumber
                  inputId="ap-sim-amount"
                  [formControl]="simulatorAmount"
                  mode="decimal"
                  [min]="0"
                  [minFractionDigits]="2"
                  [maxFractionDigits]="2"
                  locale="en-US"
                  inputStyleClass="text-right"
                />
                <p-inputgroup-addon>USD</p-inputgroup-addon>
              </p-inputgroup>
            </div>

            @if (preview()?.simulation; as sim) {
              @if (sim.error; as simError) {
                <p-message severity="error" icon="pi pi-times-circle">{{ localized(simError) }}</p-message>
              } @else {
                @if (sim.approval; as approval) {
                  @if (approval.isAutoApproved) {
                    <p-message severity="success" icon="pi pi-check-circle">
                      {{ 'ApprovalPolicies_Simulator_Auto' | translateWF: 'No approver applies: approved automatically.' }}
                    </p-message>
                  } @else {
                    <ul class="flex flex-col gap-2">
                      @for (slot of approval.slots; track slot.id) {
                        <li class="flex items-center justify-between gap-2">
                          <span>{{ slotLabel(slot) }}</span>
                          @if (slot.isRequired) {
                            <p-tag severity="info" [value]="'ApprovalPolicies_IsRequired' | translateWF: 'Required'" />
                          } @else {
                            <p-tag severity="secondary" [value]="'ApprovalPolicies_Optional' | translateWF: 'Optional'" />
                          }
                        </li>
                      }
                    </ul>
                    @if (approval.minimumApprovals > 0) {
                      <small>{{ 'ApprovalPolicies_Simulator_Minimum' | translateWF: 'Optional approvals needed' }}: {{ approval.minimumApprovals }}</small>
                    }
                  }
                }
              }
            } @else {
              <small>{{ 'ApprovalPolicies_Simulator_Hint' | translateWF: 'Type an amount to see who would approve.' }}</small>
            }

            <small>
              {{ 'ApprovalPolicies_Simulator_Note' | translateWF: 'Amounts are compared in USD at the document’s own rate when it is submitted.' }}
            </small>
          </div>
        </p-panel>
      </div>
    </form>
  </div>
</main>
